<template>
  <Content
    class="shipment-entry"
    :show-toolbar="!!selectedClient"
    remove-card-wrapper
  >
    <template #header>
      <RygenButton
        as="router-link"
        mode="tertiary"
        :to="{ name: 'shipmentList' }"
        :icon="PrimeIcons.ARROW_LEFT"
        :label="t('shipments.backToAllShipments')"
        class="back-button"
      />

      <div class="flex justify-content-between align-items-center">
        <div class="content-header-left flex flex-column flex-gap-1">
          <div
            class="content-heading-container flex flex-gap-1 align-items-center"
          >
            <OrderOrShipmentTemplateControls
              v-if="authorization.hasAuthority('orderTemplate', 'read')"
              :key="`template-controls-${selectedClient?.naturalId}`"
              v-model:selected-template="selectedTemplate"
              :edited="isDirty"
              :header-text="
                t('common.newParam', { param: t('shipments.shipments') })
              "
              header-icon="map-marked-alt"
              @discard-changes="onOrderTemplateDiscardChanges"
            />
            <template v-else>
              <HeaderIcon icon="map-marked-alt" />
              <h1 id="content-heading" tabindex="-1">
                {{ t('common.newParam', { param: t('shipments.shipments') }) }}
              </h1>
            </template>

            <TagContainer
              v-model="shipment.tags"
              :loading="tagOptions.loading"
              :options="tagOptions.data"
              :allow-deletes="canDeleteTags"
              @add="onAddTag"
              @delete="
                requestDeleteConfirmation({
                  message: t('tags.tagWarningParam', {
                    param: t('common.ordersAndShipments'),
                  }),
                  accept: () => onDelete($event),
                })
              "
            />
          </div>
        </div>
      </div>
    </template>

    <template #toolbar>
      <EntryToolbar
        v-model:batch-amount="batchCreate.numShipments"
        :save-button-label="
          !selectedClient
            ? t('common.createParam', { param: t('shipments.shipments') })
            : t('common.createParamForClient', {
                param: t('shipments.shipments'),
                client: selectedClient?.name,
              })
        "
        :disabled="!selectedClient"
        :loading="isAnyLoading"
        allow-batch
        @save="onSave"
      />
    </template>

    <BlockUI :blocked="isSaving" full-screen />
    <BlockUI
      :key="`main-content-${selectedClient?.naturalId}`"
      :blocked="!selectedClient"
    >
      <StopEntrySection
        id="stops"
        ref="stopEntrySection"
        v-model="shipment.stops"
        :errors="v$.stops.$errors"
        :server-errors="stopErrors"
        :show-order-reference-numbers="isOrderRefNumFromShipmentEntryEnabled"
        addable
        @rearrange="onRearrangeStops"
        @update:model-value="onUpdateStops"
      />

      <LineItemEntrySection
        id="line-items"
        ref="lineItemEntrySection"
        v-model="shipment.lineItems"
        :server-errors="lineItemErrors"
        :disable-default-reference-numbers="!!selectedTemplate"
        type="Shipment"
      />

      <BillToEdit
        id="bill-to"
        v-model="shipment.billTo"
        class="entry-section"
        expanded
      />

      <div class="entry-section flex flex-gap-1">
        <div class="flex flex-column flex-gap-1 w-6">
          <BasicInformationEntrySection id="information" v-model="shipment" />

          <FreightAllowanceSection
            id="freight-allowance"
            v-model="shipment.freightAllowance"
            :entity="shipment"
            :selected-client="selectedClient"
          />
        </div>

        <div class="flex flex-column flex-gap-1 w-6">
          <Section
            id="accessorials"
            :header="t('referenceData.accessorials', 2)"
          >
            <Card>
              <template #content>
                <AccessorialsEdit
                  v-model="shipment.requestedAccessorials"
                  class="w-6"
                />
              </template>
            </Card>
          </Section>

          <Section
            id="reference-numbers"
            :header="t('referenceData.referenceNumbers', 2)"
          >
            <Card>
              <template #content>
                <ReferenceNumbersEdit
                  v-model="shipment.referenceNumbers"
                  :errors="v$.referenceNumbers.$errors"
                  :levels="
                    isOrderRefNumFromShipmentEntryEnabled
                      ? [
                          ReferenceNumberTypeLevel.ORDER,
                          ReferenceNumberTypeLevel.SHIPMENT,
                        ]
                      : [ReferenceNumberTypeLevel.SHIPMENT]
                  "
                />
              </template>
            </Card>
          </Section>

          <Section
            v-if="$hasAuthority('glCode', 'manualInput')"
            id="gl-codes"
            :header="t('glCodes.glCodes', 2)"
          >
            <Card>
              <template #content>
                <GLCodesEdit
                  v-model="shipment.glCodes"
                  :errors="v$.glCodes.$errors"
                  class="w-6"
                />
              </template>
            </Card>
          </Section>
        </div>
      </div>
    </BlockUI>

    <AddressValidationResultModal
      :key="`address-validation-result-${selectedClient?.naturalId}`"
      v-model:visible="addressValidation.showModal"
      :class="[
        addressValidation.currentLocation,
        { 'bill-to': addressValidation.currentLocation === 'billTo' },
      ]"
      :validation-result="addressValidation.validationResult"
      @accept-corrected-address="updateLocationWithValidationResult(true)"
      @keep-original-address="updateLocationWithValidationResult(false)"
    />

    <BatchCreateSuccessModal
      :key="`batch-create-success-${selectedClient?.naturalId}`"
      :visible="batchCreate.showModal"
      :ids="batchCreate.shipmentIds"
      type="Shipment"
      @update:visible="onModalVisibilityChange"
    />

    <ChangeDomainModal
      v-model:visible="isChangeDomainModalVisible"
      :selected-domain="selectedClient"
      :selectable-domain-types="[DomainType.Client]"
      :domain-options="clients.data"
      :header="t('common.selectParam', { param: t('clients.clients') })"
      position="center"
      closable
      @change-domain="onClientChange"
    >
      <template v-if="isDirty && !!selectedClient" #message>
        <Message severity="warn" icon="pi pi-exclamation-triangle">
          {{
            t('common.selectingClientWillResetParam', {
              param: t('shipments.shipments'),
            })
          }}
        </Message>
      </template>
    </ChangeDomainModal>
  </Content>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import authorization from '@rygen/rygen-ui/authorization';
import {
  minLength,
  serverValidationPlaceholder,
  useDeleteConfirmation,
  useToast,
  useUid,
  useUnsavedChangesConfirmation,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  AddressValidationResult,
  DailyHoursOfOperation,
  DomainReference,
  DomainTree,
  DomainType,
  ErrorResponse,
  FieldError,
  HoursOfOperation,
  Location,
  Optional,
  ReferenceNumberTypeLevel,
  ShippingPointDetails,
  ShippingPointType,
  Tag,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useNewShipmentWithDefaults } from '@/composables';
import {
  EmbeddableLocation,
  ManualShipment,
  OrderOrShipmentTemplate,
  Shipment,
  ShipmentLineItem,
  ShipmentStop,
  ShipmentStopType,
} from '@/model';
import { useClientStore } from '@/stores/Client';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useLocationGroupStore } from '@/stores/LocationGroup';
import { useMessageStore } from '@/stores/Message';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { useTagStore } from '@/stores/Tag';
import { isBadRequest } from '@/util';

import AccessorialsEdit from '@/components/accessorial/AccessorialsEdit.vue';
import BasicInformationEntrySection from '@/components/entry/BasicInformationEntrySection.vue';
import StopEntrySection from '@/components/entry/StopEntrySection.vue';
import FreightAllowanceSection from '@/components/FreightAllowance/FreightAllowanceSection.vue';
import GLCodesEdit from '@/components/glCode/GLCodesEdit.vue';
import LineItemEntrySection from '@/components/lineItem/LineItemEntrySection.vue';
import BillToEdit from '@/components/location/BillToEdit.vue';
import OrderOrShipmentTemplateControls from '@/components/orderOrShipmentTemplate/OrderOrShipmentTemplateControls.vue';
import ReferenceNumbersEdit from '@/components/referenceNumber/ReferenceNumbersEdit.vue';
import BatchCreateSuccessModal from '@/components/ui/BatchCreateSuccessModal.vue';
import EntryToolbar from '@/components/ui/EntryToolbar.vue';
import AddressValidationResultModal from '@rygen/rygen-ui/components/address/AddressValidationResultModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import ClientAvatar from '@rygen/rygen-ui/components/client/ClientAvatar.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import HeaderIcon from '@rygen/rygen-ui/components/content/HeaderIcon.vue';
import ChangeDomainModal from '@rygen/rygen-ui/components/domain/ChangeDomainModal.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import BlockUI from 'primevue/blockui';
import Card from 'primevue/card';
import Message from 'primevue/message';

type LocationKey = Extract<keyof Shipment, 'origin' | 'destination' | 'billTo'>;

const OWNING_CLIENT_MESSAGE_KEY = 'shipment-entry-owning-client-message';

const { clientTreeMap, clients, loadClients } = useClientStore();
const { selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const { resolveLocationGroups } = useLocationGroupStore();
const { addMessage, removeMessage } = useMessageStore();
const { clearParentEntity, setParentEntity } = useParentEntityStore();
const router = useRouter();
const { loadTags, tags, tagOptions, deleteTag } = useTagStore();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const lineItemEntrySection = useTemplateRef('lineItemEntrySection');
const stopEntrySection = useTemplateRef('stopEntrySection');
const toast = useToast();
const { uid } = useUid('shipment-entry');
const isOrderRefNumFromShipmentEntryEnabled = useFlag(
  'order-ref-num-from-shipment-entry-feat-flag',
);

const selectedTemplate: Ref<OrderOrShipmentTemplate | undefined> = ref();
const {
  isLoading: isLoadingDefaults,
  isDirty,
  shipment,
  shipmentWithDefaults,
} = useNewShipmentWithDefaults(selectedTemplate);

const rules = {
  stops: {
    minLength: minLength(2, t('shipments.stops', 2)),
  },
  referenceNumbers: { serverValidationPlaceholder },
  glCodes: { serverValidationPlaceholder },
};
const { externalResults, v$, validate } = useValidations(shipment, rules, uid);

const isLoading = ref(false);
const isSaving = ref(false);
const isRedirecting = ref(false);
const isChangeDomainModalVisible = ref(false);
const stopErrors: Ref<Optional<ErrorResponse>> = ref();
const lineItemErrors: Ref<Optional<ErrorResponse>> = ref();

useUnsavedChangesConfirmation({
  isDirtyCallback: async () =>
    !!selectedClient.value &&
    !isSaving.value &&
    !isRedirecting.value &&
    isDirty.value,
});

const selectedClient: Ref<DomainTree | undefined> = ref();

const batchCreate = ref({
  showModal: false,
  numShipments: 1,
  shipmentIds: [] as number[],
});

const addressValidation = ref({
  showModal: false,
  validationResult: new AddressValidationResult(),
  currentLocation: 'origin' as LocationKey,
});

const canDeleteTags = computed((): boolean =>
  authorization.hasAuthority('tag', 'manage'),
);

const isAnyLoading = computed(() => {
  return isLoading.value || isLoadingDefaults.value || isSaving.value;
});

const getErrorMessages = (errors: string[] | FieldError | FieldError[]) =>
  Array.isArray(errors)
    ? errors.map((err) => (typeof err === 'string' ? err : err.message))
    : [errors.message];

const filterServerErrorsByField = (
  errorResponse: ErrorResponse,
  errorEntries: [string, string[]][],
  fieldKey: string,
) => {
  const filteredEntries = errorEntries.filter(([field]) =>
    field.includes(`${fieldKey}[`),
  );

  return {
    ...errorResponse,
    errorDetails: {
      fieldErrors: Object.fromEntries(filteredEntries),
    },
  };
};

const setServerErrors = (errorResponse: ErrorResponse) => {
  if (!errorResponse?.errorDetails?.fieldErrors) {
    return;
  }

  // convert Java field errors to vuelidate errors
  const allEntries = Object.entries(errorResponse.errorDetails.fieldErrors).map<
    [string, string[]]
  >(([field, fieldErrors]) => {
    // `field` looks like 'shipment.stops[0].requestedAppointmentEarliest' or 'shipment.stops'
    const parsedField = field.replace('shipment.', '');
    const errorMessages = getErrorMessages(fieldErrors);

    return [parsedField, errorMessages];
  });

  // Stops and line items are validated in a different scope so we have to pass
  // their server errors down to them and let them render the errors
  stopErrors.value = filterServerErrorsByField(
    errorResponse,
    allEntries,
    'stops',
  );
  lineItemErrors.value = filterServerErrorsByField(
    errorResponse,
    allEntries,
    'lineItems',
  );

  const nonStopEntries = allEntries
    .filter(
      ([field]) => !field.includes('stops[') && !field.includes('lineItems['),
    )
    .map<[string, string[]]>(([field, fieldErrors]) => {
      const fieldPath = field.split('.');
      const parsedField = fieldPath[fieldPath.length - 1];

      return [parsedField, fieldErrors];
    });

  externalResults.value = Object.fromEntries(nonStopEntries);
};

const onUpdateStops = () => {
  stopErrors.value = undefined;
};

const onRearrangeStops = (sequenceNumberMappings: Record<number, number>) => {
  shipment.value.lineItems.forEach((l) => {
    if (l.pickupStopSequenceNumber) {
      l.pickupStopSequenceNumber =
        sequenceNumberMappings[l.pickupStopSequenceNumber];
    }

    if (l.dropOffStopSequenceNumber) {
      l.dropOffStopSequenceNumber =
        sequenceNumberMappings[l.dropOffStopSequenceNumber];
    }
  });
};

const validateAllLocations = async (): Promise<boolean> => {
  let areAllValid = true;
  if (shipment.value.origin.location.requiresValidation) {
    areAllValid &&= await validateLocation(
      shipment.value.origin.location,
      'origin',
    );
  }
  if (areAllValid && shipment.value.destination.location.requiresValidation) {
    areAllValid &&= await validateLocation(
      shipment.value.destination.location,
      'destination',
    );
  }
  if (areAllValid && shipment.value.billTo?.location.requiresValidation) {
    areAllValid &&= await validateLocation(
      shipment.value.billTo.location,
      'billTo',
    );
  }

  return areAllValid;
};

const onOrderTemplateDiscardChanges = () => {
  shipment.value = new ManualShipment({
    ...(selectedTemplate.value?.templateData as ManualShipment),
    owner: selectedClient.value,
  });
  nextTick(
    () => (shipmentWithDefaults.value = new ManualShipment(shipment.value)),
  );
};

const convertStopTypeToShippingPointType = (
  stopType: ShipmentStopType,
): ShippingPointType => {
  if (stopType === ShipmentStopType.PICKUP) {
    return ShippingPointType.SHIPPER;
  } else if (stopType === ShipmentStopType.DROP_OFF) {
    return ShippingPointType.RECEIVER;
  } else {
    const msg = `ShipmentStopType ${stopType} not supported here`;
    console.error(msg);
    throw new Error(msg);
  }
};

const saveProductsIfNecessary = async () => {
  await Promise.all(
    shipment.value.lineItems
      .filter((li) => li.saveToProductList)
      .map(saveProductToProductList),
  );
};

const saveToAddressBookIfNecessary = async () => {
  shipment.value.stops
    .filter((s) => s.saveToAddressBook)
    .forEach(async (stop) => {
      const stopType = stop.stopType;
      if (!stopType) {
        return;
      }

      const shippintPointType = convertStopTypeToShippingPointType(stopType);
      const savedLocation = await saveShippingPointToAddressBook(
        stop,
        shippintPointType,
      );
      stop.id = savedLocation?.id;
    });
};

const onSave = async () => {
  stopErrors.value = undefined;
  lineItemErrors.value = undefined;
  if (!(await validate())) {
    return;
  }

  shipment.value.referenceNumbers = shipment.value.referenceNumbers.filter(
    (refNum) => refNum?.type?.naturalId && refNum.value,
  );

  try {
    isSaving.value = true;
    if (!(await validateAllLocations())) {
      return;
    }
    await saveToAddressBookIfNecessary();
    await saveProductsIfNecessary();
    if (batchCreate.value.numShipments === 1) {
      const created = await api.shipment.create(shipment.value);
      isRedirecting.value = true;
      router
        .push({
          name: 'shipmentDetail',
          params: {
            shipmentId: `${created.id}`,
          },
        })
        .then(() =>
          toast.addSuccess({
            detail: t('messages.successSavingParam', {
              param: t('shipments.shipments'),
            }),
          }),
        )
        .then(() => (isRedirecting.value = false));
    } else {
      batchCreate.value.shipmentIds = await api.shipmentBatch.create(
        shipment.value,
        batchCreate.value.numShipments,
      );
      isRedirecting.value = true;
      batchCreate.value.showModal = true;
    }
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    const errorResponse = e.response?.data as ErrorResponse;
    if (isBadRequest(e)) {
      setServerErrors(errorResponse);
    } else {
      const errorDetail = errorResponse.exceptionMessage;
      console.error('Error saving shipment', shipment.value, e);
      toast.addError({
        detail:
          t('errors.savingParam', {
            param: t('shipments.shipments'),
          }) + (errorDetail ? ` - ${errorDetail}` : ''),
        error: e,
      });
    }
  } finally {
    isSaving.value = false;
  }
};

const saveShippingPointToAddressBook = async (
  shipmentStop: ShipmentStop,
  type: ShippingPointType,
): Promise<Location | undefined> => {
  try {
    const dailyHoursOfOperation = new DailyHoursOfOperation({
      open: true,
      from:
        shipmentStop.requestedAppointmentEarliest?.toFormat('HH:mm:ss') ??
        '08:00:00',
      to:
        shipmentStop.requestedAppointmentLatest?.toFormat('HH:mm:ss') ??
        '17:00:00',
    });

    const groups = resolveLocationGroups(
      shipmentStop.location?.locationGroupIds ?? [],
    );

    const location = new Location({
      id: shipmentStop.location?.locationId,
      name: shipmentStop.location?.locationName,
      address: shipmentStop.location?.toAddress(),
      code: shipmentStop.location?.code,
      shippingPointDetails: new ShippingPointDetails({
        ...shipmentStop.shippingPointDetails,
        defaultAccessorials: shipmentStop.requestedAccessorials,
        hoursOfOperation: new HoursOfOperation({
          hoursSameForAllDays: false,
          monday: dailyHoursOfOperation,
          tuesday: dailyHoursOfOperation,
          wednesday: dailyHoursOfOperation,
          thursday: dailyHoursOfOperation,
          friday: dailyHoursOfOperation,
          saturday: new DailyHoursOfOperation({ open: false }),
          sunday: new DailyHoursOfOperation({ open: false }),
        }),
      }),
      groups,
    });
    location.shippingPointDetails?.shippingPointTypes?.push(type);

    return location.id
      ? api.shippingPoint.update(location)
      : api.shippingPoint.save(location);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    const isFiconLocation = !!shipmentStop.location.locationId;
    console.error(
      `Error ${isFiconLocation ? 'updating' : 'saving'} location for shipping point`,
      e,
    );
    toast.addError({
      detail: t(
        isFiconLocation ? 'errors.updatingParam' : 'errors.savingParam',
        { param: t('locations.locations') },
      ),
      error: e,
    });
    return undefined;
  }
};

const saveProductToProductList = async (lineItem: ShipmentLineItem) => {
  try {
    return lineItem.productId
      ? await api.product.update(lineItem.toProduct())
      : await api.product.save(lineItem.toProduct());
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(
      `Error ${lineItem.productId ? 'updating' : 'saving'} product`,
      e,
    );
    toast.addError({
      detail: t(
        lineItem.productId ? 'errors.updatingParam' : 'errors.savingParam',
        {
          param: t('products.products', 1),
        },
      ),
      error: e,
    });
  }
};

const validateLocation = async (
  location: EmbeddableLocation,
  currentLocation: LocationKey,
): Promise<boolean> => {
  try {
    isLoading.value = true;
    addressValidation.value.currentLocation = currentLocation;
    addressValidation.value.validationResult =
      await api.shippingPoint.validateAddress(location.toAddress());

    if (addressValidation.value.validationResult.errors?.length) {
      addressValidation.value.showModal = true;
      return false;
    } else {
      location.hasBeenValidated = true;
      return true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return false;
    }

    console.error('Error validating address', e);
    return true;
  } finally {
    isLoading.value = false;
  }
};

const updateLocationWithValidationResult = (acceptCorrection: boolean) => {
  const location = shipment.value[addressValidation.value.currentLocation];
  if (!location) {
    return;
  }

  if (acceptCorrection) {
    location.location = EmbeddableLocation.fromAddress(
      addressValidation.value.validationResult.correctedAddress?.address,
      location.location?.locationName,
      addressValidation.value.validationResult.geocodeResponse?.timeZoneLocale,
    );
  }
  location.location.hasBeenValidated = true;
  addressValidation.value.showModal = false;
  onSave();
};

const onModalVisibilityChange = (visible: boolean) => {
  if (batchCreate.value.showModal && !visible) {
    router
      .push({ name: 'shipmentList' })
      .then(() => (isRedirecting.value = false));
  }
  nextTick(() => (batchCreate.value.showModal = visible));
};

const onAddTag = (tag: string) => {
  tags.value.data?.push(new Tag({ name: tag }));
};

const onClientChange = (newClient: DomainReference) => {
  selectedClient.value = newClient.id
    ? clientTreeMap.value.get(newClient.id)
    : undefined;
};

const onDelete = (tag: string) => {
  const tagToDelete = tags.value.data?.find((t: Tag) => t.name === tag);
  if (tagToDelete) {
    deleteTag(tagToDelete);
  }
};

const updateSelectedClient = (selectedDomain: DomainReference | undefined) => {
  selectedClient.value =
    selectedDomain?.domainType === DomainType.Client && selectedDomain.id
      ? clientTreeMap.value.get(selectedDomain.id)
      : undefined;

  isChangeDomainModalVisible.value = !selectedClient.value;
};

watch(selectedDomain, async (newVal: DomainReference | undefined) => {
  await loadClients();
  updateSelectedClient(newVal);
});

watch(
  selectedClient,
  async (newVal: DomainTree | undefined) => {
    if (newVal) {
      shipment.value = new ManualShipment({
        owner: newVal?.asDomainReference(),
      });
      setParentEntity(shipment);
      loadTags({ bypassCache: true });

      addMessage({
        id: OWNING_CLIENT_MESSAGE_KEY,
        severity: 'info',
        text: t('common.assignedToParam', { param: newVal.name }),
        iconSlot: () =>
          h(ClientAvatar, {
            name: newVal.name,
            logo: newVal.branding?.avatar?.signedUrl,
          }),
        onClick: () => (isChangeDomainModalVisible.value = true),
      });
    }
  },
  { immediate: true },
);

watch(isChangeDomainModalVisible, (newVal: boolean, oldVal: boolean) => {
  if (oldVal && !newVal && !selectedClient.value) {
    router.back();
  }
});

watch(
  selectedTemplate,
  (newVal: OrderOrShipmentTemplate | undefined) => {
    if (!isEqual(shipment.value, newVal?.templateData)) {
      nextTick(async () => {
        v$.value.$reset();
        await stopEntrySection?.value?.reset();
        await lineItemEntrySection?.value?.reset();
      });
    }
  },
  { immediate: true },
);

watch(
  () => batchCreate.value.numShipments,
  (newVal: number | null) => {
    if (!newVal) {
      batchCreate.value.numShipments = 1;
    }
  },
);

onMounted(async () => {
  await loadClients();
  updateSelectedClient(selectedDomain.value);
});

onBeforeUnmount(() => {
  clearParentEntity();
  removeMessage(OWNING_CLIENT_MESSAGE_KEY);
});
</script>

<style>
.shipment-entry {
  --p-card-body-padding: var(--sp-3) var(--sp-6);

  .content-header {
    .back-button + div {
      /* this is the row that spans the full width of the page */

      .content-header-left {
        align-items: flex-start;
        width: fit-content;
      }
    }
  }

  .content-container {
    .detail-section {
      padding: 0;
      border: none;
    }

    .entry-section {
      & + .entry-section {
        margin: var(--sp-6) 0 0;
      }

      .detail-section + .detail-section {
        --section-header-margin: var(--sp-6) 0;
      }
    }

    .line-item-summary-card {
      --p-card-body-padding: var(--sp-6);
    }

    .freight-allowance.suggestion-button {
      margin-top: var(--sp-2);

      .p-button-label {
        width: 100%;
        text-decoration: none !important;
      }
    }
  }

  .p-blockui-mask.p-overlay-mask {
    background-color: rgb(from var(--p-surface-100) r g b / 0.65);
    /*
    There's a bug with the PrimeVue component where the animation class isn't
    removed so it prevents the style above from being applied
    */
    animation: none;
  }
}
</style>
